// src/modules/fire/Quotation/QuotationPresentation.tsx

import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useVForm, VForm } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { LayoutBaseDePagina } from "../../../../shared/layouts";

import { useAddressLookup } from "../../hooks/useAddressLookup";
import { useDocumentLookup } from "../../hooks/useDocumentLookup";

import { FireSummaryCard } from "./FireSummaryCard";

import { useUnifiedCoverages } from "./hooks/useUnifiedCoverages";
import { useFireAssistancesLoader } from "./hooks/useFireAssistancesLoader";
import { useFireCalculation } from "./hooks/useFireCalculation";

import { QuoteForm } from "./QuoteForm";
import { FireQuotationData } from "./types/fire";
import { useCoverageTypeFields } from "./hooks/useCoverageTypeFields";
import { useFireQuotationLoader } from "./hooks/useFireQuotationLoader";
import { fireQuoteSchema } from "./schemas/fireQuoteSchema";
import { showToastHelper } from "../utils/showToastHelper";
import * as yup from "yup";
import { useFireSave } from "./hooks/useFireSave";
import { useFireContract } from "./hooks/useFireContract";
import ModalFireQuoteDetails from "../../modals/ModalFireQuoteDetails";
import { ModalFireConfirmContract } from "../../modals/ModalFireConfirmContract";


export default function QuotationPresentation() {
	// =====================================================================
	// ROTAS / FORM
	// =====================================================================
	const params = useParams();
	const search = useSearchParams()[0];
	const { formRef } = useVForm();

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const toastHandler = useToast();

	const quotationId = params?.id || search.get("id") || undefined;
	const mode: "create" | "edit" = quotationId ? "edit" : "create";

	// =====================================================================
	// ESTADOS PRINCIPAIS
	// =====================================================================
	const [loadingData, setLoadingData] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const TOKIO_ID = "6877e0b3-23ee-4c8c-89ad-ab21643cede1";

	const [fireQuoteData, setFireQuoteData] = useState<FireQuotationData | null>(null);
	const [brokerageIdCode, setBrokerageIdCode] = useState<string>("");
	const [brokerageComissionPercent, setBrokerageComissionPercent] = useState<number>(0);

	const [clientId, setClientId] = useState<string | null>(null);
	const [clientName, setClientName] = useState<string | null>(null);

	const [propertyType, setPropertyType] = useState<string>("");
	const [occupationId, setOccupationId] = useState<string>("");
	const [assistanceId, setAssistanceId] = useState<string | null>(null);

	const [rentValue, setRentValue] = useState<string>("0");
	const [prolaboreValue, setProlaboreValue] = useState<string>("0");
	const [installmentNumber, setInstallmentNumber] = useState<number>(1);

	const [propertysMap, setPropertysMap] = useState<Array<{ value: string; label: string }>>([]);
	const [occupationsMap, setOccupationsMap] = useState<Array<{ value: string; label: string }>>([]);
	const [assistancesMap, setAssistancesMap] = useState<Array<{ value: string; label: string }>>([]);
	const [rawGroups, setRawGroups] = useState<any[]>([]);

	const [predialPercent, setPredialPercent] = useState<string>("80");
	const [contentPercent, setContentPercent] = useState<string>("20");

	const [isLoaderReady, setIsLoaderReady] = useState(false);

	const [detailsModalOpen, setDetailsModalOpen] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [selectedQuote, setSelectedQuote] = useState<FireQuotationData | null>(null);


	// =====================================================================
	// CEP LOOKUP
	// =====================================================================
	const { handleCepLookup } = useAddressLookup(formRef);

	// =====================================================================
	// DOCUMENT LOOKUP
	// =====================================================================
	const { handleDocumentLookup } = useDocumentLookup(formRef);

	// =====================================================================
	// CARREGAMENTO DA COTAÇÃO (apenas no EDIT)
	// =====================================================================
	useFireQuotationLoader({
		mode,
		quotationId,
		clientId,
		setClientId,
		setClientName,
		formRef,
		toastHandler,
		TOKIO_ID,
		propertyType,
		setFireQuoteData,
		setPropertyType,
		setOccupationId,
		setAssistanceId,
		setBrokerageIdCode,
		setBrokerageComissionPercent,
		setPropertysMap,
		setOccupationsMap,
		setRawGroups,
		setLoadingData,
		setError,
		setRentValue,
		setProlaboreValue,
		setPredialPercent,
		setContentPercent,
		setIsLoaderReady,
	});

	// =====================================================================
	// UNIFIED COVERAGES LOAD
	// =====================================================================
	const { coverages: currentCoverages, setCoverages: setCurrentCoverages, loading: loadingCoverages } = useUnifiedCoverages({
		mode,
		fireQuoteData,
		clientId,
		companyId: TOKIO_ID,
		propertyType,
		rentValue,
	});

	// =====================================================================
	// CONTRATACAO
	// =====================================================================
	const { handleContractQuotation, isContracting } = useFireContract({
		formRef,
		toastHandler,
	});

	// =====================================================================
	// COVERAGE TYPE FIELDS (Prédio/Conteúdo)
	// =====================================================================
	const { coverageType, showOutras, handleTypeChange, handlePredialBlur } =
		useCoverageTypeFields({
			mode,
			formRef,
			predialPercent,
			contentPercent,
			setPredialPercent,
			setContentPercent,
			isLoaderReady,
		});

	// 🆕 Coberturas para o Summary - só atualiza em cálculo bem-sucedido
	const [summaryCoverages, setSummaryCoverages] = useState<any[]>([]);

	// 🆕 Versão das coberturas para detectar mudanças manuais
	const [coverageVersion, setCoverageVersion] = useState(0);

	// 🆕 Wrapper para setCurrentCoverages que incrementa versão
	const handleCoverageChange = (action: React.SetStateAction<any[]>) => {
		setCurrentCoverages(action);
		setCoverageVersion(v => v + 1); // Incrementa versão em mudanças manuais
	};

	// =====================================================================
	// ASSISTÊNCIAS
	// =====================================================================
	useFireAssistancesLoader({
		clientId,
		propertyType,
		rawGroups,
		TOKIO_ID,
		setAssistancesMap,
		setAssistanceId,
		mode,
		formRef,
	});

	// =====================================================================
	// CÁLCULO DA COTAÇÃO
	// =====================================================================
	const { result, loadingCalc, calcError } = useFireCalculation({
		mode,
		formRef,
		toastHandler,
		clientId,
		companyId: TOKIO_ID,
		propertyType,
		occupationId,
		assistanceId,
		rentValue,
		prolaboreValue,
		brokerageIdCode,
		brokerageComissionPercent,
		coverages: currentCoverages,
		setCoverages: setCurrentCoverages, // Voltar a usar direto (sem incrementar versão)
		fireQuoteData,
		loadingCoverages,
		loadingData,
		coverageVersion,
		predialPercent,
		contentPercent,
	});

	const { handleSaveQuotation, isSaving } = useFireSave({
		formRef,
		toastHandler,
	});

	// 🆕 Sincronizar summaryCoverages com currentCoverages quando cálculo é bem-sucedido
	useEffect(() => {
		if (result && !loadingCalc && !calcError) {
			// Cálculo bem-sucedido (sem erro) - atualizar summary
			setSummaryCoverages(currentCoverages);
		} else if (!result && currentCoverages.length > 0) {
			// Primeira carga (sem result ainda) - usar currentCoverages
			setSummaryCoverages(currentCoverages);
		}
		// Se loadingCalc ou calcError, NÃO atualiza (mantém valores anteriores)
	}, [result, loadingCalc, calcError, currentCoverages.length]);

	// =====================================================================
	// INICIALIZAR PARCELAS NO MODO EDIT
	// =====================================================================
	useEffect(() => {
		if (mode === "edit" && fireQuoteData?.installment_number) {
			setInstallmentNumber(fireQuoteData.installment_number);
		}
	}, [mode, fireQuoteData]);


	// =====================================================================
	// VALIDAÇÃO E SALVAMENTO
	// =====================================================================
	const handleValidateAndSave = async () => {
		const formData = formRef.current?.getData?.() ?? {};
		try {
			await fireQuoteSchema.validate(formData, { abortEarly: false });
			formRef.current?.setErrors({});
			await handleSaveQuotation({
				clientId,
				companyId: TOKIO_ID,
				brokerageId: Number(fireQuoteData?.brokerageId ?? brokerageIdCode ?? 0),
				propertyTypeId: propertyType,
				totalQuotation: result?.totalQuotation ?? 0,
				isLoadingExternal: loadingData || isSaving,
				quotationId,
				selectionData: {
					predialPercent,
					contentPercent,
					rentValue,
					prolabore: prolaboreValue,
					installments: installmentNumber,
					assistanceId,
					occupationId,
				},
				activeCoverages: summaryCoverages,
				transformDateToISO: (date?: string) => {
					if (!date) return null;
					try {
						return new Date(date).toISOString();
					} catch {
						return null;
					}
				},
				quotationResult: result,
				companyData: {
					comission_broker: String(brokerageComissionPercent),
				} as any,
			});

		} catch (errors: any) {
			const validationErrors: any = {};

			if (errors instanceof yup.ValidationError) {
				errors.inner.forEach((error: any) => {
					if (error.path) validationErrors[error.path] = error.message;
				});
			}
			formRef.current?.setErrors(validationErrors);
			console.error("Validation errors:", validationErrors);
		}
	};


	// =====================================================================
	// RENDER
	// =====================================================================
	return (
		<LayoutBaseDePagina
			titulo={mode === "create" ? "Nova Cotação de seguro incêndio" : "Atualização de cotação de seguro incêndio"}
			subtitulo="Altere as informações da cotação conforme necessário"
			barraDeFerramentas={<></>}
		>
			<VForm ref={formRef} id="formFireQuote" onSubmit={() => { }} onPointerEnterCapture={formRef} onPointerLeaveCapture={formRef} placeholder={""}>
				<Box
					sx={{
						p: 1,
						display: "flex",
						flexDirection: isSmallScreen ? "column" : "row",
						height: isSmallScreen ? "auto" : "calc(98vh - 120px)",
						overflowY: "auto",
					}}
				>
					{/* COLUNA ESQUERDA — FORMULÁRIO */}
					<Box
						sx={{
							flexGrow: 1,
							overflowY: "auto",
							"&::-webkit-scrollbar": { display: "none" },
							mr: 2,
							maxWidth: isSmallScreen ? "100%" : "calc(100% - 550px)",
						}}
					>
						<QuoteForm
							formRef={formRef}
							clientNameDefault={mode === "edit" ? clientName : undefined}
							propertys={propertysMap}
							assistances={assistancesMap}
							occupations={occupationsMap}
							onClientSelected={(value) => setClientId(value)}
							onPropertyTypeChange={(value) => setPropertyType(value)}
							onAssistaceChange={(value) => setAssistanceId(value)}
							onOccupationChange={(value) => setOccupationId(value)}
							onChangeCep={(cep) => handleCepLookup(cep)}
							coverages={currentCoverages}
							onCoveragesChange={handleCoverageChange}
							rentValue={rentValue}
							onRentValueChange={(value) => setRentValue(value)}
							onProlaboreChange={(value) => setProlaboreValue(value)}
							onInstallmentChange={(value) => setInstallmentNumber(value)}
							coverageType={coverageType}
							predialPercent={predialPercent}
							contentPercent={contentPercent}
							showOutras={showOutras}
							onTenantDocumentLookup={(document) => handleDocumentLookup(document, "tenant")}
							onLessorDocumentLookup={(document) => handleDocumentLookup(document, "lessor")}
							onCoverageTypeChange={handleTypeChange}
							onPredialBlur={handlePredialBlur}
						/>
					</Box>

					{/* COLUNA DIREITA — RESUMO */}
					<Box
						sx={{
							width: isSmallScreen ? "100%" : "500px",
							overflowY: "auto",
							p: 1,
						}}
					>
						<FireSummaryCard
							coverages={(summaryCoverages ?? []).filter((c: any) => c.enabled && (c.coverage_value ?? 0) > 0)}
							discount={result?.discount ?? 0}
							isLoading={loadingCalc || loadingCoverages || loadingData}
							totalQuotation={result?.totalQuotation ?? 0}
							installments={installmentNumber}
							onSave={handleValidateAndSave}
							saveLabel="Salvar Cotação"
							canSaveExternal={true}
							onContract={() => {
								if (!fireQuoteData) return;
								setSelectedQuote(fireQuoteData);
								setDetailsModalOpen(true);
							}}
							canContract={
								Boolean(quotationId) &&
								!isSaving &&
								!loadingData &&
								!loadingCalc &&
								!isContracting
							}
						/>
					</Box>
				</Box>
			</VForm>

			<LoadingComp isopen={loadingData} />

			{selectedQuote && (
				<ModalFireQuoteDetails
					open={detailsModalOpen}
					onClose={() => setDetailsModalOpen(false)}
					data={selectedQuote}
					isLoading={isContracting}
					onClickContract={() => {
						setConfirmModalOpen(true);
					}}
				/>
			)}

			{selectedQuote && (
				<ModalFireConfirmContract
					open={confirmModalOpen}
					onClose={() => setConfirmModalOpen(false)}
					data={selectedQuote}
					loading={isContracting}
					onConfirm={async () => {
						await handleContractQuotation({
							quotationId,
							isLoadingExternal: loadingData || isSaving,
						});

						setConfirmModalOpen(false);
						setDetailsModalOpen(false);
					}}
				/>
			)}

		</LayoutBaseDePagina >
	);
}
